/**
 * SubmissionDetailModal — view + edit a Telesales submission.
 *
 * Used from the Telesales-Home's "Submissions" tab and from the Director's
 * dashboard. Scoping is enforced by the backend (agent/director/admin).
 * Locked statuses (routed / assigned / won / lost) render in read-only mode.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X as XIcon, Save, Mail, Archive as ArchiveIcon, Loader2, Phone, AlertTriangle,
  Lock, History, CalendarClock, FileText, Zap, ChevronUp, CheckCircle2, XCircle, Sun,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const inputCls = "h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-zinc-50 focus:border-[#00E575] outline-none text-[13px] font-medium";
const labelCls = "block text-[12px] uppercase tracking-widest text-zinc-200 mb-1 font-bold";

const STATUS_LABEL = {
  pending_review: "Direktor-Freigabe",
  new: "Neu",
  routed: "An Vertrieb",
  assigned: "Zugewiesen",
  archived: "Archiviert",
  sent_back: "Zurück an Agent",
  in_auction: "Auktion",
  wiedervorlage_geplant: "Wiedervorlage",
};

const YEAR_OPTIONS = ["<1970", "1970-1990", "1991-2010", "2011-2020", ">2020"];
const RENO_OPTIONS = [
  { key: "unsaniert",   label: "Unsaniert" },
  { key: "teilsaniert", label: "Teilsaniert" },
  { key: "saniert",     label: "Saniert" },
  { key: "kfw_neubau",  label: "KfW-Neubau" },
];
const HEATING_OPTIONS = ["gas", "oel", "pellet", "holz", "fernwaerme", "waermepumpe", "strom", "sonstige"];
const PACKAGE_OPTIONS = ["guenstig", "komfort", "premium"];


export default function SubmissionDetailModal({ submissionId, onClose, onSaved }) {
  const { user } = useAuth();
  const canPvOverride = ["admin", "super_admin", "telesales_director"].includes(user?.role || "") || user?.is_super_admin;
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [pdfLoading, setPdfLoading] = useState("");  // "" | "guenstig" | "komfort_premium"
  const [catalog, setCatalog] = useState(null);      // { heiko: [], lg: [] }
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideBrand, setOverrideBrand] = useState("");
  const [overrideKw, setOverrideKw] = useState("");
  const [overrideSaving, setOverrideSaving] = useState(false);
  const [confirmResend, setConfirmResend] = useState(null);  // pending override → ask before send
  const [salesReps, setSalesReps] = useState([]);            // [{id, first_name, last_name, role, email}]
  const [assignTo, setAssignTo] = useState("");
  const [assigning, setAssigning] = useState(false);

  // PV-Paket-Override (nur Telesales-Direktor + Admin)
  const [pvPackages, setPvPackages] = useState([]);
  const [pvOverridePick, setPvOverridePick] = useState("");
  const [pvOverrideSaving, setPvOverrideSaving] = useState(false);

  // Load WP catalog (only fetched once when modal opens)
  useEffect(() => {
    if (!submissionId) return;
    api.get("/telesales/wp-catalog").then((r) => setCatalog(r.data)).catch(() => {});
  }, [submissionId]);

  // Load PV-Pakete des Mandanten (nur wenn User Override-Rechte hat)
  useEffect(() => {
    if (!submissionId || !canPvOverride) return;
    api.get("/telesales/pv-packages")
      .then((r) => setPvPackages(r.data || []))
      .catch(() => {});
  }, [submissionId, canPvOverride]);

  // Load Sales-Reps for the assign dropdown (admin/director/manager view only)
  useEffect(() => {
    if (!submissionId) return;
    api.get("/users")
      .then((r) => {
        const reps = (r.data || []).filter(
          (u) => ["sales_rep", "sales_manager", "sales_director"].includes(u.role) &&
                 u.status !== "deleted",
        );
        setSalesReps(reps);
      })
      .catch(() => {});
  }, [submissionId]);

  const assignLead = async () => {
    if (!assignTo) {
      toast.error("Bitte einen Sales-Rep auswählen");
      return;
    }
    setAssigning(true);
    try {
      const r = await api.post(
        `/telesales/submissions/${submissionId}/assign`,
        { assigned_user_id: assignTo },
      );
      const conv = r.data.conversion || {};
      const a = r.data.assignee || {};
      toast.success(
        conv.created
          ? `Zugewiesen an ${a.first_name} ${a.last_name} · Angebot ${conv.offer_number} angelegt`
          : `Zugewiesen an ${a.first_name} ${a.last_name} (bereits konvertiert: ${conv.offer_number || "—"})`,
      );
      setData(r.data.lead);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Zuweisung fehlgeschlagen");
    } finally { setAssigning(false); }
  };

  // Pre-fill override fields whenever the current package changes
  useEffect(() => {
    const p = data?.orientation_offer?.package;
    if (p?.brand) setOverrideBrand(p.brand);
    if (p?.kw != null) setOverrideKw(String(p.kw));
  }, [data?.orientation_offer?.package?.brand, data?.orientation_offer?.package?.kw]);

  const currentPkg = data?.orientation_offer?.package;

  // "Eine Größe stärker": find next-larger kw within the same brand
  const oneStrongerOption = (() => {
    if (!currentPkg || !catalog) return null;
    const list = catalog[currentPkg.brand?.toLowerCase?.()] || [];
    const stronger = list.filter((p) => p.kw > currentPkg.kw).sort((a, b) => a.kw - b.kw)[0];
    return stronger || null;
  })();

  const applyOverride = async (brand, kw, sendEmail) => {
    setOverrideSaving(true);
    try {
      const r = await api.patch(
        `/telesales/submissions/${submissionId}/override-package`,
        { brand, kw: Number(kw), resend_email: !!sendEmail },
      );
      setData((d) => ({ ...d, orientation_offer: r.data.orientation_offer }));
      toast.success(
        sendEmail
          ? `Paket geändert + neue Mail an Kunden versandt (${brand} ${kw} kW)`
          : `Paket geändert: ${brand} ${kw} kW`,
      );
      setOverrideOpen(false);
      setConfirmResend(null);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Paket-Wechsel fehlgeschlagen");
    } finally { setOverrideSaving(false); }
  };

  const triggerOverride = (brand, kw) => {
    // Step 1: show confirmation dialog asking whether to resend mail
    setConfirmResend({ brand, kw });
  };

  // Pre-fill PV-Override-Dropdown mit aktuellem Paket bzw. Override
  useEffect(() => {
    const currentId = data?.pv_package_override_id
                   || data?.orientation_offer?.pv?.package?.id
                   || "";
    setPvOverridePick(currentId);
  }, [data?.pv_package_override_id, data?.orientation_offer?.pv?.package?.id]);

  const applyPvOverride = async (packageIdOrNull) => {
    setPvOverrideSaving(true);
    try {
      const r = await api.patch(
        `/telesales/submissions/${submissionId}/pv-override`,
        { pv_package_id: packageIdOrNull, resend_email: false },
      );
      setData((d) => ({
        ...d,
        orientation_offer: r.data.orientation_offer,
        pv_package_override_id: r.data.pv_package_override_id,
      }));
      toast.success(packageIdOrNull ? "PV-Paket überschrieben" : "PV-Auto-Wahl wiederhergestellt");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "PV-Override fehlgeschlagen");
    } finally { setPvOverrideSaving(false); }
  };

  const viewPdf = async (slot) => {
    setPdfLoading(slot);
    try {
      const r = await api.get(
        `/telesales/submissions/${submissionId}/orientation-pdf`,
        { params: { slot }, responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const win = window.open(url, "_blank");
      if (!win) {
        // Popup blocked → fallback to direct download
        const a = document.createElement("a");
        a.href = url;
        a.download = `Orientierungsangebot_${slot}.pdf`;
        a.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      let msg = "PDF konnte nicht erzeugt werden";
      // Blob responses don't auto-parse error JSON
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* ignore */ }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally { setPdfLoading(""); }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/telesales/submissions/${submissionId}`)
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setForm({
          salutation: r.data.salutation || "",
          first_name: r.data.first_name || "",
          last_name:  r.data.last_name  || "",
          email:      r.data.email      || "",
          phone:      r.data.phone      || "",
          zip:        r.data.zip        || "",
          city:       r.data.city       || "",
          street:     r.data.street     || "",
          current_heating: r.data.current_heating || "",
          consumption_value: r.data.consumption_value ?? "",
          consumption_unit: r.data.consumption_unit || "",
          living_area_sqm: r.data.living_area_sqm ?? "",
          occupants: r.data.occupants ?? "",
          construction_year: r.data.construction_year || "",
          renovation_status: r.data.renovation_status || "unsaniert",
          heating_age_years: r.data.heating_age_years ?? "",
          pv_existing: !!r.data.pv_existing,
          electricity_kwh_per_year: r.data.electricity_kwh_per_year ?? "",
          package: r.data.package || "",
          appointment_type: r.data.appointment_type || "",
          appointment_date: r.data.appointment_date || "",
          appointment_time: r.data.appointment_time || "",
          agent_note: r.data.agent_note || "",
          wiedervorlage_at: r.data.wiedervorlage_at ? r.data.wiedervorlage_at.slice(0, 16) : "",
          wiedervorlage_note: r.data.wiedervorlage_note || "",
        });
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Submission nicht gefunden"))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [submissionId]);

  const isLocked = data?.is_locked;
  const canEdit  = !!data?.can_edit;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPatch = () => {
    const patch = {};
    Object.keys(form).forEach((k) => {
      const oldVal = data?.[k];
      let newVal = form[k];
      // numeric coercion
      if (["consumption_value", "living_area_sqm", "occupants",
           "heating_age_years", "electricity_kwh_per_year"].includes(k)) {
        newVal = newVal === "" || newVal === null ? null : Number(newVal);
      }
      if (k === "wiedervorlage_at") {
        // datetime-local → ISO with Z. If empty, send null + clear flag.
        if (!newVal) {
          patch.clear_wiedervorlage = true;
          return;
        }
        newVal = new Date(newVal).toISOString();
      }
      if (newVal !== oldVal && !(newVal === "" && (oldVal == null))) {
        patch[k] = newVal;
      }
    });
    return patch;
  };

  const save = async () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      toast.info("Keine Änderungen");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch(`/telesales/submissions/${submissionId}`, patch);
      setData(r.data);
      toast.success("Gespeichert");
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const resendEmail = async () => {
    if (!window.confirm(
      "Orientierungs-PDFs neu erzeugen und per E-Mail an CC + Kunde senden?",
    )) return;
    setEmailing(true);
    try {
      const r = await api.post(`/telesales/submissions/${submissionId}/resend-email`);
      if (r.data?.ok) toast.success("E-Mail versendet");
      else toast.error(r.data?.delivery?.reason || "Versand fehlgeschlagen");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally { setEmailing(false); }
  };

  const archive = async () => {
    if (!window.confirm("Submission archivieren? Sie bleibt im Verlauf sichtbar, kann aber nicht mehr bearbeitet werden.")) return;
    setArchiving(true);
    try {
      await api.post(`/telesales/submissions/${submissionId}/archive`);
      toast.success("Archiviert");
      onSaved?.({});
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Archivierung fehlgeschlagen");
    } finally { setArchiving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-stretch sm:items-center justify-center sm:p-3"
      onClick={() => !saving && !emailing && !archiving && onClose?.()}
      data-testid="submission-detail-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-3xl max-h-[100vh] sm:max-h-[92vh] bg-[#18181B] border-t sm:border border-zinc-800 sm:rounded-2xl flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-zinc-800">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-[15px] truncate">
                {loading ? "Lade…" : `${form.first_name} ${form.last_name}` || "Submission"}
              </h3>
              {data?.status && (
                <span className="shrink-0 px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest bg-zinc-800 text-zinc-300">
                  {STATUS_LABEL[data.status] || data.status}
                </span>
              )}
              {isLocked && (
                <span className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-bold uppercase tracking-widest bg-amber-500/15 text-amber-300">
                  <Lock size={9} /> gesperrt
                </span>
              )}
            </div>
            <div className="text-[12px] text-zinc-300 mt-0.5">
              {data?.agent_name && <span>Agent: {data.agent_name} · </span>}
              {data?.received_at && new Date(data.received_at).toLocaleString("de-DE")}
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded" data-testid="submission-modal-close">
            <XIcon size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>
        ) : (
          <>
            {/* Scrollable body */}
            <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-5">
              {isLocked && (
                <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/30 text-[12px] text-amber-200 flex gap-2 font-medium">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>
                    Lead bereits weitergeleitet (Status: <strong>{STATUS_LABEL[data.status] || data.status}</strong>).
                    Änderungen sind gesperrt, damit der zuständige Vertriebsmitarbeiter mit stabilen Daten arbeitet.
                    Du kannst sie weiter ansehen.
                  </div>
                </div>
              )}

              {/* Mail-Versand-Status — gibt dem Agent jederzeit Klarheit, ob
                  das Orientierungsangebot beim Kunden ankam. */}
              <MailStatusBlock data={data} />

              {/* Kunde */}
              <Block title="Kunde">
                <Grid>
                  <Field label="Anrede"><select disabled={isLocked} value={form.salutation} onChange={(e) => set("salutation", e.target.value)} className={inputCls}><option value="Herr">Herr</option><option value="Frau">Frau</option><option value="Divers">Divers</option><option value="">—</option></select></Field>
                  <Field label="Vorname *" colSpan={2}><input disabled={isLocked} className={inputCls} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} /></Field>
                  <Field label="Nachname *" colSpan={2}><input disabled={isLocked} className={inputCls} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} /></Field>
                </Grid>
                <Grid>
                  <Field label="E-Mail" colSpan={3}><input disabled={isLocked} type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} /></Field>
                  <Field label="Telefon" colSpan={2}><input disabled={isLocked} className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} /></Field>
                </Grid>
                <Grid>
                  <Field label="PLZ"><input disabled={isLocked} className={inputCls} value={form.zip} onChange={(e) => set("zip", e.target.value)} /></Field>
                  <Field label="Ort" colSpan={2}><input disabled={isLocked} className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
                  <Field label="Straße" colSpan={2}><input disabled={isLocked} className={inputCls} value={form.street} onChange={(e) => set("street", e.target.value)} /></Field>
                </Grid>
              </Block>

              {/* Heizsituation */}
              <Block title="Heizung & Verbrauch">
                <Grid>
                  <Field label="Aktuelle Heizung" colSpan={2}>
                    <select disabled={isLocked} value={form.current_heating} onChange={(e) => set("current_heating", e.target.value)} className={inputCls}>
                      <option value="">—</option>
                      {HEATING_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </Field>
                  <Field label="Verbrauch / Jahr"><input disabled={isLocked} type="number" className={inputCls} value={form.consumption_value} onChange={(e) => set("consumption_value", e.target.value)} /></Field>
                  <Field label="Einheit">
                    <select disabled={isLocked} value={form.consumption_unit} onChange={(e) => set("consumption_unit", e.target.value)} className={inputCls}>
                      <option value="">—</option>
                      <option value="kwh">kWh</option><option value="m3">m³</option><option value="liter">Liter</option><option value="kg">kg</option><option value="raummeter">Raummeter</option>
                    </select>
                  </Field>
                  <Field label="Wohnfläche m²"><input disabled={isLocked} type="number" className={inputCls} value={form.living_area_sqm} onChange={(e) => set("living_area_sqm", e.target.value)} /></Field>
                </Grid>
                <Grid>
                  <Field label="Personen"><input disabled={isLocked} type="number" className={inputCls} value={form.occupants} onChange={(e) => set("occupants", e.target.value)} /></Field>
                  <Field label="Baujahr" colSpan={2}>
                    <select disabled={isLocked} value={form.construction_year} onChange={(e) => set("construction_year", e.target.value)} className={inputCls}>
                      <option value="">—</option>
                      {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </Field>
                  <Field label="Sanierungszustand" colSpan={2}>
                    <select disabled={isLocked} value={form.renovation_status} onChange={(e) => set("renovation_status", e.target.value)} className={inputCls} data-testid="modal-renovation">
                      {RENO_OPTIONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
                    </select>
                  </Field>
                </Grid>
                <Grid>
                  <Field label="Heizungs-Alter (Jahre)"><input disabled={isLocked} type="number" className={inputCls} value={form.heating_age_years} onChange={(e) => set("heating_age_years", e.target.value)} /></Field>
                  <Field label="Stromverbrauch p.a. (kWh)"><input disabled={isLocked} type="number" className={inputCls} value={form.electricity_kwh_per_year} onChange={(e) => set("electricity_kwh_per_year", e.target.value)} /></Field>
                  <Field label="PV vorhanden">
                    <label className="flex items-center gap-2 h-10 px-3 bg-[#09090B] border border-zinc-700 rounded-lg cursor-pointer">
                      <input disabled={isLocked} type="checkbox" checked={form.pv_existing} onChange={(e) => set("pv_existing", e.target.checked)} className="accent-amber-400" />
                      <span className="text-[12px] text-zinc-300 font-medium">Ja</span>
                    </label>
                  </Field>
                </Grid>
                {data?.orientation_offer?.estimated_heat_load_kw && (
                  <div className="text-[12px] text-zinc-300 mt-1">
                    Aktuelle Heizlast-Schätzung: <strong className="text-[#00E575]">{data.orientation_offer.estimated_heat_load_kw} kW</strong>
                    {" · "}Paket-Vorschlag wird beim Speichern neu berechnet.
                  </div>
                )}
              </Block>

              {/* Paket + Termin */}
              <Block title="Paket & Termin">
                <Grid>
                  <Field label="Paket" colSpan={2}>
                    <select disabled={isLocked} value={form.package} onChange={(e) => set("package", e.target.value)} className={inputCls}>
                      {PACKAGE_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Termintyp"><select disabled={isLocked} value={form.appointment_type} onChange={(e) => set("appointment_type", e.target.value)} className={inputCls}><option value="vor_ort">Vor Ort</option><option value="telefon">Telefon</option></select></Field>
                  <Field label="Datum"><input disabled={isLocked} type="date" className={inputCls} value={form.appointment_date} onChange={(e) => set("appointment_date", e.target.value)} /></Field>
                  <Field label="Uhrzeit"><input disabled={isLocked} type="time" className={inputCls} value={form.appointment_time} onChange={(e) => set("appointment_time", e.target.value)} /></Field>
                </Grid>
              </Block>

              {/* Wiedervorlage */}
              <Block title="Wiedervorlage" icon={CalendarClock}>
                <Grid>
                  <Field label="Datum & Uhrzeit" colSpan={2}>
                    <input
                      disabled={isLocked}
                      type="datetime-local"
                      className={inputCls}
                      value={form.wiedervorlage_at}
                      onChange={(e) => set("wiedervorlage_at", e.target.value)}
                      data-testid="modal-wiedervorlage-at"
                    />
                  </Field>
                  <Field label="Notiz" colSpan={3}>
                    <input
                      disabled={isLocked}
                      className={inputCls}
                      placeholder="z.B. „Ehepartner muss noch entscheiden"
                      value={form.wiedervorlage_note}
                      onChange={(e) => set("wiedervorlage_note", e.target.value)}
                    />
                  </Field>
                </Grid>
                <div className="text-[10.5px] text-zinc-500 mt-1">
                  Gesetzte Wiedervorlagen erscheinen sortiert nach Datum/Uhrzeit auf dem Telesales-Dashboard.
                </div>
              </Block>

              {/* WP-Paket-Override — Agent kann die Empfehlung manuell anpassen */}
              {currentPkg && !data?.locked && (
                <Block title="WP-Empfehlung" icon={Zap}>
                  <div className="rounded-xl bg-[#09090B] border border-zinc-700 p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="text-[12px] uppercase tracking-widest text-zinc-300">Aktuell</div>
                        <div className="text-[14px] font-bold text-zinc-100 truncate">
                          {currentPkg.brand} {currentPkg.kw}&nbsp;kW
                        </div>
                        <div className="text-[12px] text-zinc-400 truncate">{currentPkg.name}</div>
                      </div>
                      {data?.orientation_offer?.package_override && (
                        <span className="px-2 py-0.5 rounded-full text-[9.5px] uppercase tracking-widest font-bold bg-amber-500/15 text-amber-300 border border-amber-500/40 shrink-0">
                          manuell
                        </span>
                      )}
                    </div>

                    {/* Quick action: one size bigger */}
                    {oneStrongerOption && (
                      <button
                        type="button"
                        onClick={() => triggerOverride(oneStrongerOption.brand, oneStrongerOption.kw)}
                        disabled={overrideSaving}
                        data-testid="wp-override-stronger"
                        className="w-full mb-2 h-10 px-3 rounded-lg bg-[#00E575]/10 hover:bg-[#00E575]/20 border border-[#00E575]/40 text-[#00E575] text-[12.5px] font-bold inline-flex items-center justify-center gap-2 transition disabled:opacity-50"
                      >
                        <ChevronUp size={14} />
                        Eine Größe stärker: {oneStrongerOption.brand} {oneStrongerOption.kw}&nbsp;kW
                      </button>
                    )}

                    {/* Toggle full dropdown */}
                    {!overrideOpen ? (
                      <button
                        type="button"
                        onClick={() => setOverrideOpen(true)}
                        data-testid="wp-override-toggle"
                        className="text-[12px] text-zinc-300 hover:text-zinc-100 underline underline-offset-2"
                      >
                        … oder freies Paket wählen
                      </button>
                    ) : (
                      <div className="space-y-2 pt-2 border-t border-zinc-800">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className={labelCls}>Marke</label>
                            <select
                              value={overrideBrand}
                              onChange={(e) => setOverrideBrand(e.target.value)}
                              data-testid="wp-override-brand"
                              className={inputCls}
                            >
                              <option value="HEIKO">HEIKO Thermal Plus</option>
                              <option value="LG">LG Therma V R290</option>
                            </select>
                          </div>
                          <div>
                            <label className={labelCls}>Leistung (kW)</label>
                            <select
                              value={overrideKw}
                              onChange={(e) => setOverrideKw(e.target.value)}
                              data-testid="wp-override-kw"
                              className={inputCls}
                            >
                              {(catalog?.[overrideBrand?.toLowerCase()] || []).map((p) => (
                                <option key={p.kw} value={p.kw}>
                                  {p.kw} kW — {p.name.replace(`${overrideBrand} `, "")}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end pt-1">
                          <button
                            type="button"
                            onClick={() => setOverrideOpen(false)}
                            className="h-9 px-3 rounded-lg text-[12px] text-zinc-400 hover:text-zinc-100 font-medium"
                          >
                            Abbrechen
                          </button>
                          <button
                            type="button"
                            onClick={() => triggerOverride(overrideBrand, overrideKw)}
                            disabled={overrideSaving || !overrideBrand || !overrideKw}
                            data-testid="wp-override-apply"
                            className="h-9 px-4 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] disabled:opacity-50"
                          >
                            Übernehmen
                          </button>
                        </div>
                      </div>
                    )}
                    <p className="text-[10.5px] text-zinc-500 mt-2 leading-snug">
                      Empfehlung basiert auf der berechneten Heizlast
                      ({data?.orientation_offer?.estimated_heat_load_kw} kW). Bei der Änderung
                      werden KfW und Wirtschaftlichkeits-Rechnung automatisch neu erstellt.
                    </p>
                  </div>
                </Block>
              )}

              {/* PV-Paket-Override — nur Telesales-Direktor + Admin,
                  nur wenn Kunde PV-interessiert ist und ein PV-Block existiert */}
              {canPvOverride && data?.orientation_offer?.pv?.applicable && !data?.locked && (
                <Block title="PV-Paket überschreiben" icon={Sun}>
                  <div className="rounded-xl bg-[#09090B] border border-zinc-700 p-3">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="text-[12px] uppercase tracking-widest text-zinc-300">Aktuell</div>
                        <div className="text-[14px] font-bold text-zinc-100 truncate" data-testid="pv-current-pkg-name">
                          {data.orientation_offer.pv.package?.name || "—"}
                        </div>
                        <div className="text-[12px] text-zinc-400 truncate">
                          {data.orientation_offer.pv.package?.target_kwp} kWp
                          {" · "}
                          {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(data.orientation_offer.pv.package?.price_eur || 0)}
                        </div>
                      </div>
                      {data?.pv_package_override_id && (
                        <span className="px-2 py-0.5 rounded-full text-[9.5px] uppercase tracking-widest font-bold bg-amber-500/15 text-amber-300 border border-amber-500/40 shrink-0" data-testid="pv-override-badge">
                          manuell
                        </span>
                      )}
                    </div>
                    <label className={labelCls}>Paket wählen</label>
                    <select
                      value={pvOverridePick}
                      onChange={(e) => setPvOverridePick(e.target.value)}
                      data-testid="pv-override-select"
                      className={inputCls}
                    >
                      <option value="">— Auto-Auswahl (System) —</option>
                      {pvPackages.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} · {p.target_kwp} kWp · {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(p.price_eur || 0)}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2 justify-end pt-2">
                      {data?.pv_package_override_id && (
                        <button
                          type="button"
                          onClick={() => applyPvOverride(null)}
                          disabled={pvOverrideSaving}
                          data-testid="pv-override-reset"
                          className="h-9 px-3 rounded-lg border border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-zinc-500 text-[12px] disabled:opacity-50 font-medium"
                        >
                          Auto-Wahl wiederherstellen
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => applyPvOverride(pvOverridePick || null)}
                        disabled={pvOverrideSaving || (!pvOverridePick && !data?.pv_package_override_id)}
                        data-testid="pv-override-apply"
                        className="h-9 px-4 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] disabled:opacity-50 inline-flex items-center gap-1.5"
                      >
                        {pvOverrideSaving ? <Loader2 size={12} className="animate-spin" /> : null}
                        Übernehmen
                      </button>
                    </div>
                    <p className="text-[10.5px] text-zinc-500 mt-2 leading-snug">
                      PV-Auto-Auswahl basiert auf {data.orientation_offer.pv.sizing?.target_kwp_needed} kWp Bedarf.
                      Nach dem Übernehmen werden Ertrag, Einsparung und Amortisation neu berechnet.
                    </p>
                  </div>
                </Block>
              )}

              {/* Orientierungsangebote — Premium-PDFs (günstig + Premium-Komfort) */}
              {data?.orientation_offer?.estimated_heat_load_kw && (
                <Block title="Orientierungsangebote (PDF)" icon={FileText}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <button
                      type="button"
                      onClick={() => viewPdf("guenstig")}
                      disabled={!!pdfLoading}
                      data-testid="modal-pdf-guenstig"
                      className="h-12 px-4 rounded-xl bg-[#09090B] border border-zinc-700 hover:border-[#00E575]/50 hover:bg-zinc-900 text-left transition disabled:opacity-50 flex items-center gap-3"
                    >
                      {pdfLoading === "guenstig"
                        ? <Loader2 size={16} className="animate-spin text-[#00E575] shrink-0" />
                        : <FileText size={16} className="text-[#00E575] shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-bold text-zinc-100 leading-tight">
                          HEIKO Thermal Plus
                        </div>
                        <div className="text-[10.5px] text-zinc-500 uppercase tracking-widest">
                          {'Tier „günstig"'}
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => viewPdf("komfort_premium")}
                      disabled={!!pdfLoading}
                      data-testid="modal-pdf-komfort"
                      className="h-12 px-4 rounded-xl bg-[#09090B] border border-zinc-700 hover:border-[#00E575]/50 hover:bg-zinc-900 text-left transition disabled:opacity-50 flex items-center gap-3"
                    >
                      {pdfLoading === "komfort_premium"
                        ? <Loader2 size={16} className="animate-spin text-[#00E575] shrink-0" />
                        : <FileText size={16} className="text-[#00E575] shrink-0" />}
                      <div className="min-w-0">
                        <div className="text-[12.5px] font-bold text-zinc-100 leading-tight">
                          LG Therma V R290
                        </div>
                        <div className="text-[10.5px] text-zinc-500 uppercase tracking-widest">
                          {'Tier „Premium-Komfort"'}
                        </div>
                      </div>
                    </button>
                  </div>
                  <div className="text-[10.5px] text-zinc-500 mt-1">
                    Die PDFs werden bei jedem Klick frisch mit den aktuellen Daten erzeugt — identisch zu dem,
                    was per E-Mail an den Kunden ging.
                  </div>
                </Block>
              )}

              {/* Zuweisen an Sales-Rep (für admin/director/manager-Sichten) */}
              {data?.id && salesReps.length > 0 && (
                <Block title="Zuweisen an Sales-Rep" icon={Zap}>
                  {data?.assigned_to_user_id ? (
                    <div className="rounded-xl border border-[#00E575]/40 bg-[#00E575]/5 px-3 py-2.5 mb-2">
                      <div className="text-[11.5px] uppercase tracking-widest text-[#00E575] font-bold mb-0.5">Aktuell zugewiesen</div>
                      <div className="text-[13px] font-semibold text-zinc-100">
                        {(salesReps.find((u) => u.id === data.assigned_to_user_id) || {}).first_name || ""}{" "}
                        {(salesReps.find((u) => u.id === data.assigned_to_user_id) || {}).last_name || ""}
                      </div>
                      {data?.offer_number && (
                        <div className="text-[11.5px] text-zinc-300 font-mono mt-0.5">Angebot {data.offer_number}</div>
                      )}
                    </div>
                  ) : null}
                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      value={assignTo}
                      onChange={(e) => setAssignTo(e.target.value)}
                      className={inputCls}
                      data-testid="assign-user-picker"
                    >
                      <option value="">— Sales-Rep auswählen —</option>
                      {salesReps.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name} ({u.role})
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={!assignTo || assigning}
                      onClick={assignLead}
                      data-testid="assign-submit-btn"
                      className="h-10 px-4 rounded-lg bg-[#00E575] text-zinc-950 font-bold text-[12.5px] hover:bg-[#00C964] disabled:opacity-40 inline-flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      {assigning ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                      {data?.assigned_to_user_id ? "Neu zuweisen" : "Jetzt zuweisen"}
                    </button>
                  </div>
                  <p className="text-[12px] text-zinc-300 mt-2 leading-snug font-medium">
                    Beim Zuweisen entsteht aus dem Orientierungsangebot automatisch ein
                    bearbeitbares Angebot beim Sales-Rep. Der TeleSales-Agent behält
                    weiterhin Lesezugriff.
                  </p>
                </Block>
              )}

              {/* Agent-Notiz */}
              <Block title="Notizen">
                <textarea
                  disabled={isLocked}
                  className={inputCls + " h-24 py-2"}
                  value={form.agent_note}
                  onChange={(e) => set("agent_note", e.target.value)}
                  placeholder="Was wurde besprochen? Spezielle Wünsche, Einwände, nächste Schritte…"
                />
              </Block>

              {/* Change-Log */}
              {Array.isArray(data?.change_log) && data.change_log.length > 0 && (
                <div className="border-t border-zinc-800 pt-3">
                  <button
                    onClick={() => setShowLog((v) => !v)}
                    className="inline-flex items-center gap-1.5 text-[12px] text-zinc-400 hover:text-zinc-200"
                    data-testid="modal-changelog-toggle"
                  >
                    <History size={12} />
                    {showLog ? "Verlauf ausblenden" : `Verlauf einblenden (${data.change_log.length})`}
                  </button>
                  {showLog && (
                    <div className="mt-2 space-y-1.5 text-[12px]" data-testid="modal-changelog">
                      {data.change_log.slice().reverse().map((e, i) => {
                        // Backwards-compat: manche Alt-Einträge nutzen `ts` statt `at`,
                        // `user_role` statt `by_name`.
                        const ts = e.at || e.ts || e.timestamp;
                        const dt = ts ? new Date(ts) : null;
                        const validDate = dt && !Number.isNaN(dt.getTime());
                        const byLabel = e.by_name || e.user_name || e.user_role || e.user_id || "System";
                        const changes = Array.isArray(e.changes) ? e.changes : [];
                        return (
                          <div key={i} className="px-2 py-1.5 bg-[#09090B] border border-zinc-800 rounded">
                            <div className="text-zinc-400">
                              <strong className="text-zinc-200">{byLabel}</strong>
                              {" · "}
                              {validDate ? dt.toLocaleString("de-DE") : (e.action || "—")}
                            </div>
                            {changes.map((c, j) => (
                              <div key={j} className="text-zinc-500 truncate">
                                {c.field}: <span className="line-through text-zinc-600">{String(c.from ?? "—")}</span> → <span className="text-zinc-200">{String(c.to ?? "—")}</span>
                              </div>
                            ))}
                            {!changes.length && e.action && (
                              <div className="text-zinc-500">Aktion: <span className="text-zinc-200">{e.action}</span></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            <div className="border-t border-zinc-800 px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-2 bg-[#0c0c0e]">
              <button
                onClick={archive}
                disabled={isLocked || archiving}
                data-testid="modal-archive-btn"
                className="h-10 px-3 rounded-lg border border-zinc-700 text-zinc-400 hover:text-red-300 hover:border-red-500/40 text-[12px] disabled:opacity-50 inline-flex items-center gap-1.5 font-medium"
              >
                {archiving ? <Loader2 size={13} className="animate-spin" /> : <ArchiveIcon size={13} />}
                Archivieren
              </button>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={resendEmail}
                  disabled={emailing}
                  data-testid="modal-resend-email-btn"
                  className="h-10 px-3 rounded-lg bg-zinc-900 border border-zinc-700 hover:border-[#00E575]/40 text-zinc-200 text-[12px] inline-flex items-center gap-1.5 font-medium"
                >
                  {emailing ? <Loader2 size={13} className="animate-spin" /> : <Mail size={13} />}
                  PDF + Mail neu senden
                </button>
                <button
                  onClick={save}
                  disabled={!canEdit || saving}
                  data-testid="modal-save-btn"
                  className="h-10 px-4 rounded-lg bg-[#00E575] hover:bg-[#00C964] text-[#09090B] font-bold text-[12px] disabled:bg-zinc-800 disabled:text-zinc-300 inline-flex items-center gap-1.5"
                >
                  {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
                  Speichern
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Bestätigungs-Dialog: Soll die neue PDF-Mail an den Kunden gehen? */}
      {confirmResend && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur flex items-end sm:items-center justify-center px-3 py-4"
          onClick={(e) => { if (e.target === e.currentTarget && !overrideSaving) setConfirmResend(null); }}
          data-testid="wp-override-confirm-modal"
        >
          <div className="bg-[#18181B] border border-zinc-700 rounded-2xl w-full max-w-md p-5 shadow-2xl">
            <h3 className="font-display text-lg font-bold text-zinc-50 mb-1">
              Paket auf {confirmResend.brand} {confirmResend.kw} kW ändern?
            </h3>
            <p className="text-[13px] text-zinc-300 leading-relaxed mb-4">
              KfW und Wirtschaftlichkeit werden neu berechnet, das PDF wird beim nächsten
              Aufruf frisch erzeugt. Soll der Kunde sofort eine neue Mail mit dem aktualisierten
              Angebot bekommen?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                disabled={overrideSaving}
                onClick={() => setConfirmResend(null)}
                data-testid="wp-override-cancel"
                className="h-11 px-4 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-[13px] font-bold disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                disabled={overrideSaving}
                onClick={() => applyOverride(confirmResend.brand, confirmResend.kw, false)}
                data-testid="wp-override-save-only"
                className="h-11 px-4 rounded-xl bg-[#09090B] border border-zinc-700 hover:bg-zinc-900 text-zinc-100 text-[13px] font-bold disabled:opacity-50"
              >
                {overrideSaving ? <Loader2 size={14} className="animate-spin inline mr-1" /> : null}
                Nur speichern
              </button>
              <button
                type="button"
                disabled={overrideSaving}
                onClick={() => applyOverride(confirmResend.brand, confirmResend.kw, true)}
                data-testid="wp-override-save-and-send"
                className="h-11 px-4 rounded-xl bg-[#00E575] hover:bg-[#00C964] text-[#09090B] text-[13px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-50"
              >
                {overrideSaving ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                Speichern + Mail senden
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function Block({ title, icon: Icon, children }) {
  return (
    <section className="space-y-2.5">
      <h4 className="font-bold text-[12px] uppercase tracking-widest text-zinc-200 inline-flex items-center gap-1.5">
        {Icon && <Icon size={12} />} {title}
      </h4>
      {children}
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">{children}</div>;
}

function Field({ label, colSpan = 1, children }) {
  const span = ["", "sm:col-span-1", "sm:col-span-2", "sm:col-span-3", "sm:col-span-4", "sm:col-span-5"][colSpan];
  return (
    <div className={`col-span-2 ${span}`}>
      <label className={labelCls}>{label}</label>
      {children}
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// MailStatusBlock — Versand-Status des Orientierungsangebots
// ────────────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function MailStatusBlock({ data }) {
  // Latest send wins: resend (if present) overrides initial. Both stored on lead.
  const initial = data?.email_delivery;
  const resend  = data?.last_email_delivery;
  const latest  = resend || initial;
  const latestAt = data?.last_email_resent_at || data?.email_sent_at;
  const recipient = data?.email || "—";

  // No delivery info at all → lead probably just captured but mail not yet processed
  if (!latest) {
    return (
      <div className="rounded-xl border border-zinc-700 bg-zinc-900/40 px-3 py-2.5 flex items-center gap-2.5" data-testid="mail-status-unknown">
        <Loader2 size={16} className="text-zinc-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-[12.5px] font-semibold text-zinc-200">Versand-Status unbekannt</div>
          <div className="text-[12px] text-zinc-300 font-medium">Erfasst, aber noch keine Bestätigung vom Mail-Server.</div>
        </div>
      </div>
    );
  }

  const ok = !!latest.sent;
  const provider = latest.provider || latest.method || "SMTP";
  const reason = latest.reason || latest.error;

  if (ok) {
    return (
      <div className="rounded-xl border border-[#00E575]/40 bg-[#00E575]/5 px-3 py-2.5 flex items-start gap-2.5" data-testid="mail-status-ok">
        <CheckCircle2 size={18} className="text-[#00E575] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-[#00E575]">
            Orientierungsangebot erfolgreich versendet
          </div>
          <div className="text-[11.5px] text-zinc-300 mt-0.5">
            an <span className="font-semibold">{recipient}</span> · {fmtDate(latestAt)}
            <span className="text-zinc-500"> · Provider: {provider}</span>
            {resend && (
              <span className="ml-1 px-1.5 py-0.5 rounded text-[9.5px] uppercase tracking-widest font-bold bg-sky-500/15 text-sky-300 border border-sky-500/30">
                neu versendet
              </span>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-rose-500/40 bg-rose-500/5 px-3 py-2.5 flex items-start gap-2.5" data-testid="mail-status-fail">
      <XCircle size={18} className="text-rose-300 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-bold text-rose-200">
          Mail-Versand fehlgeschlagen
        </div>
        <div className="text-[11.5px] text-zinc-300 mt-0.5">
          an <span className="font-semibold">{recipient}</span>
          {latestAt && <span className="text-zinc-500"> · {fmtDate(latestAt)}</span>}
        </div>
        {reason && (
          <div className="text-[12px] text-rose-300/80 mt-1 leading-snug">
            Grund: {String(reason).slice(0, 200)}
          </div>
        )}
        <div className="text-[10.5px] text-zinc-500 mt-1">
          {'Bitte unten den Button „PDF + Mail neu senden" nutzen.'}
        </div>
      </div>
    </div>
  );
}
